/**
 * operationLinkIntegrity.js (C14, §8.7) — detección EN CLIENTE de si lo que el usuario acaba de
 * cambiar en un nodo diverge de la versión de biblioteca a la que está vinculado (#130 S55, S56;
 * historia 152 S31, S36).
 *
 * ⚠️ Esto es UX, no decisión: la autoridad es el servidor (`RN-03.17`, P3), que evalúa
 * `LinkBreakingChange` contra la versión vinculada dentro del guardado. Aquí solo se adelanta el
 * veredicto para que la cabecera del nodo (C7) lo diga EN EL MOMENTO y el usuario no lo descubra
 * al guardar.
 *
 * El criterio se evalúa POR COMPLEMENTO, igual que en Sun: diverge TODO lo que no esté en la lista
 * tolerada. Así, un campo nuevo del job diverge por omisión en vez de colarse como tolerado — que
 * es justo lo que S55 exige y lo que hace que la regla envejezca bien.
 *
 * Historia 152 (S31, S36 · RN-01.14, RN-01.15): **la ruptura automática desaparece**. El mismo
 * criterio, campo a campo, ya no lleva el nodo a "Embedded" sino de `SYNCHRONIZED` a `CUSTOMIZED`,
 * conservando operación y versión de origen. Por eso `resolveOperationLinkState` devuelve ESTADO y
 * no el par `linked/broken`, y por eso `TOLERATED_JOB_FIELDS`, `operationSignature` y
 * `breaksOperationLink` NO se tocan (POL1, IN5: S52, S53 y S54 de #130 siguen verdes sin editarse).
 */

/**
 * Los CUATRO campos de configuración que NO rompen el vínculo (`ToleratedJobFields`,
 * 03-dominio §3.2 · S52), con los sellos de autoría que acompañan al index.
 */
export const TOLERATED_JOB_FIELDS = [
  'indexValue', 'draftBy', 'proposalBy', 'approvedBy', 'approved',
  'jobOrder', 'positionInLine', 'planningGroup',
  // Story 183 (S26 · RN11.1, D18): the night mark is a planning decision about this job of this
  // workflow, not part of what the library operation defines.
  'nightJob'
]

/** El tipo de recurso que es asignación de BOM DEL WORKFLOW, nunca de la operación (S54, RN-03.5). */
const WORKFLOW_RESOURCE = 'WORKFLOW_RESOURCE'

const resourceType = (resource) => resource?.resourceType?.name || resource?.resourceType || resource?.type || ''

/**
 * La foto del job que SÍ define la operación: sin los campos tolerados y sin las filas de recurso
 * del BOM del workflow.
 */
export const operationSignature = (jobReference = {}) => {
  const signature = {}
  Object.keys(jobReference || {}).forEach((field) => {
    if (TOLERATED_JOB_FIELDS.includes(field)) return
    if (field === 'resources') {
      signature.resources = (jobReference.resources || []).filter((resource) => resourceType(resource) !== WORKFLOW_RESOURCE)
      return
    }
    signature[field] = jobReference[field]
  })
  return signature
}

/**
 * ¿Rompe el vínculo el estado actual del job respecto al que tenía al vincularse?
 *
 * @param {Object} linkedJobReference El job tal y como quedó al vincular (arrastre o carga)
 * @param {Object} currentJobReference El job tal y como está ahora en el editor
 * @returns {boolean} true si el cambio es de los que rompen (S55); false si es tolerado (S52, S54)
 */
export const breaksOperationLink = (linkedJobReference, currentJobReference) => {
  if (!linkedJobReference || !currentJobReference) return false
  return JSON.stringify(operationSignature(linkedJobReference)) !== JSON.stringify(operationSignature(currentJobReference))
}

/**
 * Los cuatro regímenes de `RN-G1`, tal y como los nombra el contrato (§8.3): el estado vacío es el
 * nodo **embebido sin procedencia**, que en el `NodeOperationLink` de Sun viaja como `state: null`.
 */
export const OPERATION_LINK_STATE = {
  SYNCHRONIZED: 'SYNCHRONIZED',
  CUSTOMIZED: 'CUSTOMIZED',
  UNLINKED: 'UNLINKED',
  EMBEDDED: ''
}

/** Los dos estados con vínculo VIVO — la semántica que `linked` conserva de #130 (§8.3). */
const LIVE_STATES = [OPERATION_LINK_STATE.SYNCHRONIZED, OPERATION_LINK_STATE.CUSTOMIZED]

/**
 * El estado que la cabecera del nodo (C7) debe pintar ahora mismo (S26, S31, S36).
 *
 * Manda el `state` del servidor; el cliente solo **escala** `SYNCHRONIZED → CUSTOMIZED` en cuanto lo
 * que hay en pantalla diverge de la foto de línea base, y vuelve atrás solo si el usuario deshace
 * (S36). Nunca degrada un `CUSTOMIZED` del servidor: al cargar, la foto de línea base ES el job ya
 * personalizado, así que la ausencia de divergencia contra ella no dice nada (RN-01.16).
 *
 * Un `state` ausente con `linked` a true es un vínculo vivo recién arrastrado, todavía sin escribir
 * (Δ9): se lee `SYNCHRONIZED`, que es como nace (`RN-01.7`).
 *
 * @param {Object} operationLink `NodeOperationLink` tal y como lo devuelve Sun (§8.3)
 * @param {Object} linkedJobReference Foto del job al vincularse (o al cargar el workflow)
 * @param {Object} currentJobReference Job actual
 * @returns {{state: string, linked: boolean, code: string, versionNumber: number, originCode: string,
 *   originVersionNumber: number, customizedInClient: boolean}}
 */
export const resolveOperationLinkState = (operationLink = {}, linkedJobReference = null, currentJobReference = null) => {
  const declaredState = operationLink?.state || (operationLink?.linked ? OPERATION_LINK_STATE.SYNCHRONIZED : OPERATION_LINK_STATE.EMBEDDED)
  const origin = {
    originCode: operationLink?.originCode || '',
    originVersionNumber: operationLink?.originVersionNumber || 0
  }
  if (!LIVE_STATES.includes(declaredState)) {
    // Desvinculado: la procedencia sobrevive como historia (RN-01.18). Embebido: nada de nada (S27).
    return {
      state: declaredState === OPERATION_LINK_STATE.UNLINKED ? OPERATION_LINK_STATE.UNLINKED : OPERATION_LINK_STATE.EMBEDDED,
      linked: false,
      code: '',
      versionNumber: 0,
      ...origin,
      customizedInClient: false
    }
  }
  const customizedInClient = declaredState === OPERATION_LINK_STATE.SYNCHRONIZED &&
    breaksOperationLink(linkedJobReference, currentJobReference)
  return {
    state: customizedInClient ? OPERATION_LINK_STATE.CUSTOMIZED : declaredState,
    linked: true,
    code: operationLink?.code || '',
    versionNumber: operationLink?.versionNumber || 0,
    ...origin,
    customizedInClient
  }
}
